import { apiClient } from './api';
import type { GradingScheme, GradingComponent, FinalGrade, PracticalScore, QuizScore } from '../types/grading';

export interface RecordPracticalScorePayload {
  trainerId: string;
  score: number;
  notes?: string;
}

export const gradingService = {
  // Create grading scheme — POST /grading/scheme
  createGradingScheme: async (courseId: string, components: GradingComponent[]): Promise<GradingScheme> => {
    const response = await apiClient.post<GradingScheme>(`/grading/scheme`, { course_id: courseId, components });
    return response.data as GradingScheme;
  },

  // Get grading scheme — GET /grading/scheme/:course_id
  getGradingScheme: async (courseId: string): Promise<GradingScheme> => {
    const response = await apiClient.get<GradingScheme>(`/grading/scheme/${courseId}`);
    return response.data as GradingScheme;
  },

  // Update grading scheme — PUT /grading/scheme/:course_id
  updateGradingScheme: async (courseId: string, components: GradingComponent[]): Promise<GradingScheme> => {
    const response = await apiClient.put<GradingScheme>(`/grading/scheme/${courseId}`, { components });
    return response.data as GradingScheme;
  },

  // Record practical score — POST /grading/practical
  recordPracticalScore: async (
    enrollmentId: string,
    data: RecordPracticalScorePayload
  ): Promise<PracticalScore> => {
    const response = await apiClient.post<PracticalScore>(`/grading/practical`, {
      enrollment_id: enrollmentId,
      trainer_id: data.trainerId,
      score: data.score,
      notes: data.notes,
    });
    return response.data as PracticalScore;
  },

  // Get practical scores — GET /grading/practical/:enrollment_id
  getPracticalScores: async (enrollmentId: string): Promise<PracticalScore[]> => {
    const response = await apiClient.get<PracticalScore[]>(`/grading/practical/${enrollmentId}`);
    return response.data as PracticalScore[];
  },

  // Get practical scores batch — POST /grading/practical/batch
  getPracticalScoresBatch: async (enrollmentIds: string[]): Promise<Record<string, PracticalScore[]>> => {
    const response = await apiClient.post<Record<string, PracticalScore[]>>(
      '/grading/practical/batch',
      { enrollment_ids: enrollmentIds }
    );
    return (response.data as Record<string, PracticalScore[]>) || {};
  },

  // Get final grade — GET /grading/final/:enrollment_id
  getFinalGrade: async (enrollmentId: string): Promise<FinalGrade> => {
    const response = await apiClient.get<FinalGrade>(`/grading/final/${enrollmentId}`);
    return response.data as FinalGrade;
  },

  // Get quiz scores — GET /enrollments/:enrollment_id/quiz-scores
  getQuizScores: async (enrollmentId: string): Promise<QuizScore[]> => {
    const response = await apiClient.get<QuizScore[]>(`/enrollments/${enrollmentId}/quiz-scores`);
    return response.data as QuizScore[];
  },

  // Calculate final grade — POST /grading/calculate/:enrollment_id
  calculateFinalGrade: async (enrollmentId: string): Promise<FinalGrade> => {
    const response = await apiClient.post<FinalGrade>(`/grading/calculate/${enrollmentId}`);
    return response.data as FinalGrade;
  },
};
